import {
  HeaderButton,
  LayoutMode,
  LayoutWidth,
  MainContainerProps,
  MainTemplate,
  MenuItemProps,
  MenuProps,
} from '@easylife365/react';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import {
  usePagedApps,
  useGroupByExpiration,
  useShowEntraIdLinks,
  useLicenseInfo,
  useExpiryClassification,
} from '../../../lib/hooks';
import { AdditionalAppsMessage, ExpirationMessage, FooterSwitches } from '../../elements';

const createHeaderButton = (text: string, url: string): HeaderButton => ({
  text,
  to: url,
  target: '_blank',
  icon: faExternalLink,
});

const appRegButton = createHeaderButton(
  'Entra ID',
  'https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade/quickStartType~/null/sourceType/Microsoft_AAD_IAM'
);

const enterpriseButton = createHeaderButton(
  'Entra ID',
  'https://entra.microsoft.com/#view/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/~/AppAppsPreview'
);

const getManagedLabel = (managedLabel: string, appsLength: number) => `${managedLabel} (${appsLength})`;

const createNavItem = (
  label: string,
  headerText: string,
  navigateTo: string,
  primaryButton?: HeaderButton,
  description?: string
): MenuItemProps => ({
  label: label,
  navigateTo: navigateTo,
  noSwitch: true,
  page: {
    header: {
      text: headerText,
      primaryButton: primaryButton,
      description: description,
    },
    layoutMode: LayoutMode.LIST,
    layoutWidth: LayoutWidth.FULL,
  },
});

const createNavItemWithWarning = (
  label: string,
  items: number,
  headerText: string,
  navigateTo: string,
  itemsFoundWarning: string,
  primaryButton?: HeaderButton,
  description?: string
) => {
  const navItem: MenuItemProps = createNavItem(
    `${label} (${items})`,
    headerText,
    navigateTo,
    primaryButton,
    description
  );
  navItem.warning = items > 0 ? itemsFoundWarning : undefined;

  return navItem;
};

const MainLayout = () => {
  const { t } = useTranslation('mainLayout');
  const {
    appRegistrations,
    enterpriseApplications,
    failedAdditionalAppCount,
    isRetryingAdditionalApps,
    retryAdditionalApps,
  } = usePagedApps();
  const showEntraIdLinks = useShowEntraIdLinks();

  const {
    expiringApps: expiringAppRegistrations,
    expiredApps: expiredAppRegistrations,
    managedApps: managedAppRegistrations,
  } = useGroupByExpiration(appRegistrations);

  const {
    expiringApps: expiringEnterpriseApplications,
    expiredApps: expiredEnterpriseApplications,
    managedApps: managedEnterpriseApplications,
  } = useGroupByExpiration(enterpriseApplications);

  const { isProfessional } = useLicenseInfo();
  const { expirationThresholdDays } = useExpiryClassification();

  const menu: MenuProps = {
    categories: [
      {
        heading: t('menu.categories.overview'),
        items: [
          createNavItem(
            t('menu.items.dashboard'),
            t('menu.headers.dashboard'),
            '/dashboard',
            undefined,
            t('dashboard.description')
          ),
          createNavItem(
            t('menu.items.myTasks'),
            t('menu.headers.myTasks'),
            '/tasks',
            undefined,
            t('myTasks.description')
          ),
        ],
      },
      {
        heading: t('menu.categories.appRegistrations'),
        items: [
          createNavItem(
            t('menu.items.all'),
            t('menu.headers.appRegistrationsAll'),
            '/registeredApps',
            showEntraIdLinks ? appRegButton : undefined,
            t('appRegistrations.all.description')
          ),
          createNavItemWithWarning(
            t('menu.items.expiring'),
            expiringAppRegistrations.length,
            t('menu.headers.appRegistrationsExpiring'),
            '/registeredApps/upcoming',
            t('menu.warning.itemsFound'),
            showEntraIdLinks ? appRegButton : undefined,
            t('appRegistrations.expiring.description', { days: expirationThresholdDays })
          ),
          createNavItemWithWarning(
            t('menu.items.expired'),
            expiredAppRegistrations.length,
            t('menu.headers.appRegistrationsExpired'),
            '/registeredApps/expired',
            t('menu.warning.itemsFound'),
            showEntraIdLinks ? appRegButton : undefined,
            t('appRegistrations.expired.description')
          ),
          ...(isProfessional
            ? [
                createNavItem(
                  getManagedLabel(t('menu.items.managed'), managedAppRegistrations.length),
                  t('menu.headers.appRegistrationsManaged'),
                  '/registeredApps/managed',
                  showEntraIdLinks ? appRegButton : undefined,
                  t('appRegistrations.managed.description')
                ),
              ]
            : []),
        ],
      },
      {
        heading: t('menu.categories.enterpriseApplications'),
        items: [
          createNavItem(
            t('menu.items.all'),
            t('menu.headers.enterpriseApplicationsAll'),
            '/apps',
            showEntraIdLinks ? enterpriseButton : undefined,
            t('enterpriseApplications.all.description')
          ),
          createNavItemWithWarning(
            t('menu.items.expiring'),
            expiringEnterpriseApplications.length,
            t('menu.headers.enterpriseApplicationsExpiring'),
            '/apps/upcoming',
            t('menu.warning.itemsFound'),
            showEntraIdLinks ? enterpriseButton : undefined,
            t('enterpriseApplications.expiring.description', { days: expirationThresholdDays })
          ),
          createNavItemWithWarning(
            t('menu.items.expired'),
            expiredEnterpriseApplications.length,
            t('menu.headers.enterpriseApplicationsExpired'),
            '/apps/expired',
            t('menu.warning.itemsFound'),
            showEntraIdLinks ? enterpriseButton : undefined,
            t('enterpriseApplications.expired.description')
          ),
          ...(isProfessional
            ? [
                createNavItem(
                  getManagedLabel(t('menu.items.managed'), managedEnterpriseApplications.length),
                  t('menu.headers.enterpriseApplicationsManaged'),
                  '/apps/managed',
                  showEntraIdLinks ? enterpriseButton : undefined,
                  t('enterpriseApplications.managed.description')
                ),
              ]
            : []),
        ],
      },
    ],
    footer: {
      children: <FooterSwitches />,
    },
  };

  const mainContainerProps: MainContainerProps = {
    menu: menu,
  };

  return (
    <MainTemplate mainContainer={mainContainerProps}>
      <ExpirationMessage showExpirationInDays={14} />
      <AdditionalAppsMessage
        failedCount={failedAdditionalAppCount}
        isRetrying={isRetryingAdditionalApps}
        onRetry={retryAdditionalApps}
      />
      <Outlet />
    </MainTemplate>
  );
};

export default MainLayout;
